import * as Mui from "@mui/material";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import { useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import FormInput from "../common/FormInput";
import { nameof } from "../common/ObjectUtils";
import { RfidTypes } from "./RfidCardTypes";
import RfidCardActions from "../../service/rfidCard.actions";
import { useContext, useState } from "react";
import { ContextOperation } from "../../context/OperationContext";
import { useTranslation } from "react-i18next";
import useSnackbar from "../../hooks/useSnackbar";
import EmptyOrErrorInput from "./EmptyOrErrorInput";
import dayjs from "dayjs";

interface Props {
  open: boolean;
  onClose: () => void;
  resetForm: boolean;
  setResetForm: (val: boolean) => void;
}

const CreateRfidCard = (props: Props) => {
  const t: any = useOperationTranslation();
  const { tenant, setLoading } = useContext(ContextOperation);
  const { i18n } = useTranslation();
  const snackbar = useSnackbar();
  const [openEmptyOrErrorModal, setOpenEmptyOrErrorModal] =
    useState<boolean>(false);
  const [bodyText, setBodyText] = useState<string>("");

  const form = useForm<RfidTypes.CreateModal>({
    defaultValues: {},
  });

  const onSubmit = () => {
    let data = {
      ...form.getValues(),
      vehiclePlate: !!form.getValues().vehiclePlate
        ? form.getValues().vehiclePlate?.toUpperCase()
        : undefined,
      activationDate: !!form.getValues().activationDate
        ? dayjs(form.getValues().activationDate)?.format("YYYY-MM-DD")
        : undefined,
      deactivationDate: !!form.getValues().deactivationDate
        ? dayjs(form.getValues().deactivationDate)?.format("YYYY-MM-DD")
        : undefined,
      vehicleObjectId: !!form.getValues().vehicleObjectId
        ? form.getValues().vehicleObjectId
        : undefined,
      description: !!form.getValues().description
        ? form.getValues().description
        : undefined,
      supplierCode: !!form.getValues().supplierCode
        ? form.getValues().supplierCode
        : undefined,
      supplierDescription: !!form.getValues().supplierDescription
        ? form.getValues().supplierDescription
        : undefined,
    };

    setLoading(true);
    RfidCardActions()
      .createRfidCard(tenant, i18n.resolvedLanguage, data)
      .then((response) => {
        setLoading(false);
        props.onClose();
        snackbar.show("success", t("rfidCard.createModal.successMssg"));
        props.setResetForm(!props.resetForm);
      })
      .catch((error: any) => {
        if (
          error.response.data.code == 400 &&
          error.response.data.subCode == 7
        ) {
          setLoading(false);
          setBodyText(error.response.data.msg);
          setOpenEmptyOrErrorModal(true);
        } else {
          setLoading(false);
          snackbar.show("error", t("rfidCard.createModal.errorMssg"));
        }
      });
  };

  if (openEmptyOrErrorModal) {
    return (
      <EmptyOrErrorInput
        setOpenErrorModal={setOpenEmptyOrErrorModal}
        openErrorModal={openEmptyOrErrorModal}
        title={t("rfidCard.createModal.errorModalTitle")}
        body={bodyText}
      />
    );
  }

  return (
    <Mui.Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="lg"
    >
      <Mui.DialogTitle>
        <Mui.Grid container alignItems="center">
          <Mui.Grid item>
            <Mui.Typography variant="h5">
              {t("rfidCard.createModal.title")}
            </Mui.Typography>
          </Mui.Grid>
          <Mui.Grid item marginLeft={"auto"}>
            <Mui.IconButton onClick={props.onClose}>
              <CloseIcon />
            </Mui.IconButton>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.DialogTitle>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Mui.DialogContent>
          <Mui.Grid container spacing="16px" p={2}>
            <Mui.Grid item md={3} xs={12}>
              <FormInput
                type="text"
                label={t("rfidCard.createModal.vehicleObjectId")}
                name={nameof<RfidTypes.CreateModal>("vehicleObjectId")}
                formInstance={form}
                fullWidth
				validation={{
                  required: !!form.getValues().vehiclePlate
                    ? ""
                    : t("rfidCard.createModal.requiredObjectID"),
                }}
              />
            </Mui.Grid>
            <Mui.Grid item md={3} xs={12}>
              <FormInput
                type="text"
                label={t("rfidCard.createModal.vehiclePlate")}
                name={nameof<RfidTypes.CreateModal>("vehiclePlate")}
                formInstance={form}
                fullWidth
				validation={{
                  required: !!form.getValues().vehicleObjectId
                    ? ""
                    : t("rfidCard.createModal.requiredPlate"),
                }}

              />
            </Mui.Grid>
            <Mui.Grid item md={3} xs={12}>
              <FormInput
                type="text"
                label={t("rfidCard.createModal.code")}
                name={nameof<RfidTypes.CreateModal>("code")}
                formInstance={form}
                fullWidth
                validation={{
                  required: t("rfidCard.createModal.requiredCode"),
                }}
              />
            </Mui.Grid>
            <Mui.Grid item md={3} xs={12}>
              <FormInput
                type="text"
                label={t("rfidCard.createModal.description")}
                name={nameof<RfidTypes.CreateModal>("description")}
                formInstance={form}
                fullWidth
              />
            </Mui.Grid>
          </Mui.Grid>

          <Mui.Grid container spacing="16px" p={2}>
            <Mui.Grid item md={3} xs={12}>
              <FormInput
                type="date"
                inputFormat={"DD/MM/YYYY"}
                removeTime={true}
                label={t("rfidCard.createModal.activationDate")}
                name={nameof<RfidTypes.CreateModal>("activationDate")}
                formInstance={form}
                fullWidth
				validation={{
                  required: !!form.getValues().deactivationDate
                    ? ""
                    : t("rfidCard.createModal.requiredActivation"),
                }}

              />
            </Mui.Grid>
            <Mui.Grid item md={3} xs={12}>
              <FormInput
                type="date"
                inputFormat="DD/MM/YYYY"
                removeTime
                label={t("rfidCard.createModal.deactivationDate")}
                name={nameof<RfidTypes.CreateModal>("deactivationDate")}
                formInstance={form}
                fullWidth
				validation={{
                  required: !!form.getValues().activationDate
                    ? ""
                    : t("rfidCard.createModal.requiredDeactivation"),
                }}

              />
            </Mui.Grid>
            <Mui.Grid item md={3} xs={12}>
              <FormInput
                type="text"
                label={t("rfidCard.createModal.supplierCode")}
                name={nameof<RfidTypes.CreateModal>("supplierCode")}
                formInstance={form}
                fullWidth
              />
            </Mui.Grid>
            <Mui.Grid item md={3} xs={12}>
              <FormInput
                type="text"
                label={t("rfidCard.createModal.supplierDescription")}
                name={nameof<RfidTypes.CreateModal>("supplierDescription")}
                formInstance={form}
                fullWidth
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogContent>

        <Mui.DialogActions>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs="auto" ml="auto">
              <Mui.Button
                variant="outlined"
                onClick={props.onClose}
                size="smallPlus"
              >
                <Mui.Typography color="inherit" variant="h6">
                  {t("actionButtons.cancel")}
                </Mui.Typography>
              </Mui.Button>
            </Mui.Grid>

            <Mui.Grid item xs="auto">
              <Mui.Button type="submit" size="smallPlus">
                <Mui.Typography color="inherit" variant="h6">
                  {t("actionButtons.save")}
                </Mui.Typography>
              </Mui.Button>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogActions>
      </form>
    </Mui.Dialog>
  );
};

export default CreateRfidCard;
